import React, { useEffect, useState } from 'react';
import {
  CompanyOrder,
  CompanyOrdersPage,
  getCompanyOrders,
} from '../companyHelpers';
import style from './CompanyOrdersTab.module.css';

/* §FB44: вкладка «Заявки» сторінки компанії — заявки на переклад, заплановані
   користувачами компанії: номер, дата/час, замовник, закріплений перекладач,
   опис і статус обробки. Пагіновано; «Показати ще» довантажує. */

const PAGE_SIZE = 30;

// Запланована дата й час у київському поясі: «08.07.2026, 19:14».
const formatOrderAt = (iso: string): string => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  try {
    return d.toLocaleString('uk-UA', {
      timeZone: 'Europe/Kyiv',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return d.toLocaleString('uk-UA');
  }
};

// Статус обробки (рахує бекенд; expired — запланований час +30 хв минув).
const STATUS_VIEW: Record<
  string,
  { label: string; tone: 'ok' | 'live' | 'bad' | 'gray' }
> = {
  created: { label: 'Очікує підтвердження', tone: 'live' },
  confirmed: { label: 'Підтверджена', tone: 'ok' },
  completed: { label: 'Завершена', tone: 'ok' },
  cancelled: { label: 'Скасована', tone: 'bad' },
  expired: { label: 'Прострочена', tone: 'gray' },
  archived: { label: 'В архіві', tone: 'gray' },
};

interface CompanyOrdersTabProps {
  companyId: number;
}

const CompanyOrdersTab: React.FC<CompanyOrdersTabProps> = ({ companyId }) => {
  const [pageData, setPageData] = useState<CompanyOrdersPage | null>(null);
  const [items, setItems] = useState<CompanyOrder[]>([]);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const loadPage = async (pageIndex: number, replace: boolean) => {
    setLoading(true);
    setError(false);
    try {
      const data = await getCompanyOrders(companyId, pageIndex, PAGE_SIZE);
      setPageData(data);
      // При довантаженні прибираємо можливі дублі (список міг зсунутись новими заявками).
      setItems((prev) =>
        replace
          ? data.items
          : [
              ...prev,
              ...data.items.filter((n) => !prev.some((p) => p.id === n.id)),
            ],
      );
      setPage(pageIndex);
    } catch {
      setError(true);
      if (replace) setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPage(0, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  return (
    <div className={style.wrap}>
      <div className={style.card}>
        <div className={style.head}>
          <h5 className={style.title}>Заявки користувачів компанії</h5>
          {pageData && pageData.totalCount > 0 && (
            <span className={style.count}>{pageData.totalCount}</span>
          )}
          <button
            type="button"
            className={style.refresh}
            onClick={() => loadPage(0, true)}
            disabled={loading}
            title="Оновити список заявок"
            aria-label="Оновити список заявок"
          >
            ⟳
          </button>
        </div>
        <p className={style.subhint}>
          Заплановані виклики перекладача на конкретні дату і час; статус
          «Прострочена» — запланований час минув, а заявка не була завершена.
        </p>

        {loading && items.length === 0 && (
          <p className={style.muted}>Завантаження…</p>
        )}
        {error && items.length === 0 && !loading && (
          <p className={style.errorText}>
            Не вдалося завантажити заявки. Спробуйте ще раз.
          </p>
        )}
        {!loading && !error && items.length === 0 && (
          <p className={style.muted}>
            Заявок від користувачів цієї компанії ще не було.
          </p>
        )}

        {items.length > 0 && (
          <div className={style.list}>
            <div className={`${style.row} ${style.headRow}`}>
              <span>Дата і час</span>
              <span>№</span>
              <span>Запланував</span>
              <span>Перекладач</span>
              <span>Опис</span>
              <span>Статус</span>
            </div>
            {items.map((o) => {
              const view = STATUS_VIEW[o.status] ?? STATUS_VIEW.created;
              const toneClass =
                view.tone === 'ok'
                  ? style.tOk
                  : view.tone === 'live'
                    ? style.tLive
                    : view.tone === 'bad'
                      ? style.tBad
                      : style.tGray;
              return (
                <div key={o.id} className={style.row}>
                  <span className={style.cDate}>
                    {formatOrderAt(o.orderDate)}
                  </span>
                  <span className={style.cNum}>№{o.id}</span>
                  <span className={style.cName} title={o.clientName}>
                    {o.clientName || '—'}
                  </span>
                  <span className={style.cName} title={o.translatorName}>
                    {o.translatorName || '—'}
                  </span>
                  <span className={style.cComment} title={o.comment || undefined}>
                    {o.comment || '—'}
                  </span>
                  <span className={`${style.badge} ${toneClass}`}>
                    {view.label}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {pageData?.hasNextPage && items.length > 0 && (
          <button
            type="button"
            className={style.moreBtn}
            onClick={() => loadPage(page + 1, false)}
            disabled={loading}
          >
            {loading ? 'Завантаження…' : 'Показати ще'}
          </button>
        )}
      </div>
    </div>
  );
};

export default CompanyOrdersTab;
